import { StatusResponse } from "@0xsquid/squid-types";
import { useQueries } from "@tanstack/react-query";
import { keys } from "../../core/queries/queries-keys";
import { HistoryTxType } from "../../core/types/history";
import {
  OnRampTransactionStatus,
  OnRampTransactionStatusType,
} from "../../core/types/onramps/onramp";

import {
  fetchSwapTransactionStatus,
  getSendTxStatusRefetchInterval,
  getSwapTxStatusRefetchInterval,
  getTransactionEndStatus,
} from "../../services";
import { OnrampService } from "../../services/external/onrampAdapter";
import { getSendTransactionStatus } from "../../services/internal/sendTransactionStatus";
import { useSquidChains } from "../chains/useSquidChains";

import { SendTransactionStatus } from "../../core/types/transaction";
import {
  FINAL_TRANSACTION_STATUSES,
  TX_STATUS_CONSTANTS,
} from "../onramp/useFiatToCrypto";
import { useHistoryStore } from "../store/useHistoryStore";
import { useConfigStore } from "../store/useSquidStore";
import { useHistory } from "../user/useHistory";

export const useAllTransactionsStatus = ({ enabled }: { enabled: boolean }) => {
  const { pendingTransactions, replaceSwapTransactionStatus } = useHistory();
  const replaceTransactionStatus = useHistoryStore(
    (state) => state.replaceTransactionStatus
  );
  const config = useConfigStore((state) => state.config);
  const { findChain } = useSquidChains();
  const onrampService = new OnrampService();

  const statusQueries = pendingTransactions.map(({ txType, data }) => {
    switch (txType) {
      case HistoryTxType.SWAP: {
        return {
          queryKey: keys().swapTransactionStatus(data.transactionId),
          queryFn: async (): Promise<StatusResponse | undefined> => {
            return fetchSwapTransactionStatus({
              transaction: {
                ...data,
                transactionId:
                  data.transactionIdForStatus ?? data.transactionId,
              },
              apiUrl: config.apiUrl!,
              integratorId: config.integratorId,
            });
          },
          enabled: true,
          retry: 4,
          retryDelay: getSwapTxStatusRefetchInterval(data),
          refetchInterval: 5000,
          onSuccess: (statusResponse: StatusResponse) => {
            if (!statusResponse) return;

            const endStatus = getTransactionEndStatus({
              statusResponse,
            });

            if (!endStatus) return;

            replaceSwapTransactionStatus({
              transactionId: data.transactionId,
              statusResponse,
              status: endStatus,
            });
          },
        };
      }
      case HistoryTxType.BUY:
        return {
          queryFn: () =>
            onrampService.getTransactionStatus(
              data.orderId,
              data.toAddress,
              data.providerId
            ),
          queryKey: keys().fiatToCryptoStatus(data.orderId),
          retry: TX_STATUS_CONSTANTS.RETRY_COUNT,
          retryDelay: TX_STATUS_CONSTANTS.RETRY_DELAY,
          refetchInterval: (data: OnRampTransactionStatus | undefined) => {
            if (
              data?.status &&
              FINAL_TRANSACTION_STATUSES.includes(
                data.status as OnRampTransactionStatusType
              )
            ) {
              return false;
            }
            return TX_STATUS_CONSTANTS.REFETCH_INTERVAL;
          },
          onSuccess: (statusData: OnRampTransactionStatus) => {
            replaceTransactionStatus({
              orderId: data.orderId,
              status: statusData.status,
              transactionHash: statusData.transactionHash,
              txType: HistoryTxType.BUY,
            });
          },
          onError: () => {
            replaceTransactionStatus({
              txType: HistoryTxType.BUY,
              orderId: data.orderId,
              status: "failed",
              transactionHash: undefined,
            });
          },
          enabled: true,
        };
      case HistoryTxType.SEND:
        return {
          queryKey: keys().sendTransactionStatus(data.hash, data.token.chainId),
          queryFn: async () => {
            const chain = findChain(data.token.chainId);

            if (!chain) {
              throw new Error(
                `Could not find chain ${data.token.chainId} for tx status`
              );
            }

            return getSendTransactionStatus({
              chain,
              txHash: data.hash,
            });
          },
          refetchInterval: getSendTxStatusRefetchInterval(data.token.type),
          onSuccess: (status: SendTransactionStatus) => {
            if (status !== SendTransactionStatus.ONGOING) {
              replaceTransactionStatus({
                txType: HistoryTxType.SEND,
                status,
                hash: data.hash,
              });
            }
          },
          onError: () => {
            replaceTransactionStatus({
              txType: HistoryTxType.SEND,
              status: SendTransactionStatus.ERROR,
              hash: data.hash,
            });
          },
        };
    }
  });
  const queries = useQueries({
    queries: enabled && config.apiUrl ? statusQueries : [],
  });

  return {
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    data: queries.map((q) => q.data).filter(Boolean),
  };
};
